import React, { useEffect, useRef, useState } from 'react'
import { IoCheckmarkCircle, IoEllipseOutline, IoChevronDown, IoChevronUp } from 'react-icons/io5'

interface Feature {
  name: string
  completed: boolean
  note?: string
}

interface Phase {
  id: string
  number: string
  title: string
  status: 'complete' | 'current' | 'upcoming'
  goal: string
  features: Feature[]
  dependencies?: string[]
  exclusions?: string[]
  whyNow?: string[]
  principles?: string[]
}

interface ImprovementItem {
  id: string
  title: string
  gap: string
  deliverables: string[]
  effort: string
  references?: { name: string; url: string }[]
}

interface FeatureIdea {
  id: string
  title: string
  description: string
  effort: string
}

interface TechDebtItem {
  id: string
  title: string
  description: string
  deliverables?: string[]
  effort: string
}

interface TimelineSection {
  title: string
  items: string[]
}

interface AdminMilestone {
  id: string
  focus: string
  owner: string
  effort: string
}

interface AdminSpecSection {
  title: string
  items: string[]
}

interface Source {
  category: string
  links: { name: string; url: string; note?: string }[]
}

// High Priority Improvements
const HIGH_PRIORITY_IMPROVEMENTS: ImprovementItem[] = [
  {
    id: '12.1',
    title: 'xG Visualization & Integration',
    gap: 'xG is now shown in live stats. Advanced features pending.',
    deliverables: [
      'Display xG per team on fixture detail page ✅ DONE',
      'Show xG timeline chart (cumulative xG over match minutes)',
      'Add xG to builder angles (e.g., "Team xG last 5: 1.8/match")',
      'Enable xG-based filtering on value bets page',
    ],
    effort: '~6-8 hours remaining',
    references: [
      { name: 'Understat', url: 'https://understat.com/' },
      { name: 'xG Stat', url: 'https://www.xgstat.com/' },
    ],
  },
  {
    id: '12.2',
    title: 'Shot Maps & Visual Pitch Representations',
    gap: 'No visual representation of shots, goals, or player positions.',
    deliverables: [
      'Add shot map component showing shot locations with xG values',
      'Color-code by outcome (goal/saved/blocked/missed)',
      'Show for both teams in fixture detail',
      'Consider player heatmaps for key players',
    ],
    effort: '~16-24 hours',
    references: [
      { name: 'FotMob', url: 'https://www.fotmob.com/' },
      { name: 'Breaking The Lines', url: 'https://breakingthelines.com/opinion/advanced-analytics-meet-fans-how-betting-platforms-are-using-xg-and-heat-maps-to-engage-supporters/' },
    ],
  },
  {
    id: '12.3',
    title: 'Push Notifications & Alerts System',
    gap: '✅ DONE (Phase 6)',
    deliverables: [
      'Lineup confirmation alerts (key for player props) ✅ DONE',
      'Value bet alerts when edge > threshold',
      'Builder invalidation alerts (e.g., "Player X ruled out") ✅ DONE',
      'Goal/card alerts for tracked fixtures ✅ DONE',
    ],
    effort: '✅ Complete',
    references: [
      { name: 'OddAlerts Pro', url: 'https://oddalerts.com/pro' },
    ],
  },
  {
    id: '12.4',
    title: 'PWA Support',
    gap: 'No "Add to Home Screen" capability.',
    deliverables: [
      'Add service worker for offline capability',
      'Implement web app manifest',
      'Enable push notifications via PWA',
      'Consider app-like navigation gestures',
    ],
    effort: '~8-12 hours',
  },
]

// Medium Priority Improvements
const MEDIUM_PRIORITY_IMPROVEMENTS: ImprovementItem[] = [
  {
    id: '12.5',
    title: 'Odds Comparison Table',
    gap: 'Odds from 30+ bookmakers exist but no comparison UI.',
    deliverables: [
      'Add "Compare Odds" button per market',
      'Show table: bookmaker | odds | edge | link',
      'Highlight best odds and significant outliers',
      'Sort by value or alphabetically',
    ],
    effort: '~6-10 hours',
  },
  {
    id: '12.6',
    title: 'Dropping Odds Alerts',
    gap: 'Odds history exists but no alerting on significant moves.',
    deliverables: [
      'Detect >5% odds movement within 1 hour',
      'Badge markets with recent drops',
      'Optional notification for drops on shortlisted markets',
      'Filter value bets page by "recently dropped"',
    ],
    effort: '~12-16 hours',
    references: [
      { name: 'Odds Portal', url: 'https://www.oddsportal.com/dropping-odds/' },
    ],
  },
  {
    id: '12.7',
    title: 'Social Features — Tipster Leaderboards',
    gap: 'All tracking is private. No community engagement.',
    deliverables: [
      'Optional public profile with bet history',
      'Global leaderboard (ROI, hit rate, streak)',
      'Follow system for top performers',
      '"Copy builder" from public profiles',
    ],
    effort: '~30-40 hours',
    references: [
      { name: 'BetsWall', url: 'https://apps.apple.com/us/app/betswall-football-betting-tips/id1064425227' },
      { name: 'BettingPros', url: 'https://apps.apple.com/us/app/bettingpros-sports-betting/id1468109182' },
    ],
  },
  {
    id: '12.8',
    title: 'Auto-Generated Betslips',
    gap: 'Users manually build. No intelligent suggestions.',
    deliverables: [
      '"Generate Betslip" button with filters (min edge, max odds, market types)',
      'Suggest 3-5 leg parlays based on correlation analysis',
      'Show expected hit rate and edge for generated slip',
      'Allow quick add to builder',
    ],
    effort: '~12-16 hours',
    references: [
      { name: 'OddAlerts Betslip Generator', url: 'https://oddalerts.com/pro' },
    ],
  },
  {
    id: '12.9',
    title: 'Player Availability Filtering',
    gap: 'Currently showing markets for potentially unavailable players.',
    deliverables: [
      'Integrate /sidelined endpoint from API-Football',
      'Grey out/hide unavailable player markets',
      'Show availability badge (fit/doubt/out)',
      'Pre-match burst refresh for late team news',
    ],
    effort: '~8-12 hours',
  },
]

// Lower Priority Improvements
const LOWER_PRIORITY_IMPROVEMENTS: ImprovementItem[] = [
  {
    id: '12.10',
    title: 'Live In-Play Tips Ticker',
    gap: 'No in-play guidance. ✅ DONE (Phase 9)',
    deliverables: [
      'Display real-time ticker of high-probability lines not yet landed ✅ DONE',
      'Filter by market type, minute range ✅ DONE',
      'Show pre-match probability vs current live odds ✅ DONE',
      'Auto-refresh every 1-2 minutes ✅ DONE (WebSocket)',
    ],
    effort: '✅ Complete',
  },
  {
    id: '12.11',
    title: 'AI Pattern Detection',
    gap: '🟡 IN PROGRESS (Phase 8)',
    deliverables: [
      '"This team concedes late goals against pressing teams" ⏳',
      '"Unusually high corner average in last 3 away matches" ⏳',
      'Pattern summaries in fixture overview ⏳',
      'Keep transparent (show underlying data) ⏳',
    ],
    effort: '~60 hours (Phase 8)',
  },
  {
    id: '12.12',
    title: 'Physical Stats (FotMob-style)',
    gap: 'No distance covered, sprints, top speed.',
    deliverables: [
      'If API-Football provides physical data, extract and display',
      'Add to player profiles',
      'Use in fatigue-adjusted probability models',
    ],
    effort: '~16-24 hours',
    references: [
      { name: 'FotMob', url: 'https://www.fotmob.com/' },
    ],
  },
]

// New Feature Ideas
const FEATURE_IDEAS: FeatureIdea[] = [
  {
    id: '13.1',
    title: 'Fixture Momentum Chart',
    description: 'Visual timeline showing which team is dominating (shots, possession, xG) minute-by-minute. Useful for in-play context.',
    effort: '~12-16 hours',
  },
  {
    id: '13.2',
    title: 'Builder Templates',
    description: 'Pre-made builder configurations: "Cards Focus", "Corner Kings", "Goalscorer Special", "Under Fest".',
    effort: '~8-12 hours',
  },
  {
    id: '13.3',
    title: 'Fixture Similarity Engine',
    description: '"Find similar fixtures" based on team profiles, H2H, league position. Shows historical outcomes for context.',
    effort: '~16-24 hours',
  },
  {
    id: '13.4',
    title: 'Player Prop Comparisons',
    description: 'Side-by-side player prop lines across bookmakers with edge calculation.',
    effort: '~8-12 hours',
  },
  {
    id: '13.5',
    title: 'Regression Alerts',
    description: 'Flag teams over/under-performing xG significantly (e.g., "Liverpool xG: 18.5, Actual: 12 — regression candidate").',
    effort: '~6-10 hours',
  },
  {
    id: '13.6',
    title: 'Form Streaks & Trends',
    description: 'Visual badges: "4-match win streak", "3 consecutive overs", "Clean sheet in last 5 home matches".',
    effort: '~6-8 hours',
  },
  {
    id: '13.7',
    title: 'Referee Match History',
    description: 'Show referee\'s history with specific teams (e.g., "Michael Oliver has shown 12 cards in 3 Liverpool matches").',
    effort: '~4-6 hours',
  },
  {
    id: '13.8',
    title: 'Quick-Add Popular Markets',
    description: 'One-tap shortcuts for most popular angles per fixture. Surface high-conviction angles prominently.',
    effort: '~4-6 hours',
  },
  {
    id: '13.9',
    title: 'Multi-Fixture Builder View',
    description: 'Build across multiple fixtures in a single view. See correlation warnings across matches.',
    effort: '~16-24 hours',
  },
  {
    id: '13.10',
    title: 'Export to Bookmaker',
    description: 'Deep-link builder selections directly to bookmaker betslip. Requires affiliate integration per bookmaker.',
    effort: '~20-30 hours',
  },
]

// Technical Debt Items
const TECH_DEBT_ITEMS: TechDebtItem[] = [
  {
    id: '14.1',
    title: 'Angles → Markets Rename',
    description: 'Complete internal terminology refactor for code clarity.',
    deliverables: [
      'Backend schemas (~15 occurrences)',
      'Backend services (~80+ occurrences)',
      'Frontend components (~100+ occurrences)',
      'CSS classes (~15 occurrences)',
    ],
    effort: '~5-6 hours',
  },
  {
    id: '14.2',
    title: 'Cache Performance',
    description: 'Consider Redis for high-frequency endpoints (value bets, odds). Current DB cache works but adds latency.',
    deliverables: [
      'Evaluate Redis vs DB cache for builder responses',
      'Add Redis connection pooling if adopted',
      'Benchmark latency improvements',
    ],
    effort: '~8-12 hours',
  },
  {
    id: '14.3',
    title: 'Real-Time Updates',
    description: 'Move from polling (1-2 min) to WebSocket for live scores. Better UX for in-play features.',
    deliverables: [
      'Implement WebSocket server (FastAPI WebSocket)',
      'Live score push to connected clients',
      'Fallback to polling if WebSocket fails',
    ],
    effort: '~16-24 hours',
  },
  {
    id: '14.4',
    title: 'API Rate Limiting',
    description: 'Add proper rate limiting for external users once SaaS launches.',
    deliverables: [
      'Add rate limiting middleware (slowapi or custom)',
      'Per-user rate limits based on subscription tier',
      '429 responses with retry-after headers',
    ],
    effort: '~4-6 hours',
  },
  {
    id: '14.5',
    title: 'Error Boundary Improvements',
    description: 'Better error states in frontend for failed API calls. Currently may show blank states.',
    deliverables: [
      'Add React error boundaries to key components',
      'Show user-friendly error messages',
      'Add retry buttons for transient failures',
    ],
    effort: '~4-6 hours',
  },
]

// Recommended Timeline
const TIMELINE_SECTIONS: TimelineSection[] = [
  {
    title: 'Immediate (Phase 2 + Quick Wins)',
    items: [
      'PWA support (12.4)',
      'xG display in UI (12.1) — already in DB',
      'Affiliate deep links (Phase 2)',
      'Market availability tags (Phase 2)',
    ],
  },
  {
    title: 'Short-Term (Q1)',
    items: [
      'Shot maps component (12.2)',
      'Push notifications foundation (12.3)',
      'Odds comparison table (12.5)',
      'Player availability filtering (12.9) ✅ DONE',
    ],
  },
  {
    title: 'Medium-Term (Q2)',
    items: [
      'Dropping odds alerts (12.6)',
      'Auto-generated betslips (12.8)',
      'Builder templates (13.2)',
      'Live tips ticker (Phase 9 / 12.10) ✅ DONE',
    ],
  },
  {
    title: 'Long-Term (Q3+)',
    items: [
      'Social features / leaderboards (12.7)',
      'AI pattern detection (12.11 / Phase 8)',
      'Multi-fixture builder view (13.9)',
    ],
  },
]

const ADMIN_MILESTONES: AdminMilestone[] = [
  { id: '11.5a', focus: 'Admin foundations (RBAC, audit log, impersonation guardrails)', owner: 'Platform/Backend', effort: '~16-24 hours' },
  { id: '11.5b', focus: 'Support console (lookup, billing, timeline, notes)', owner: 'Support + Frontend', effort: '~24-32 hours' },
  { id: '11.5c', focus: 'Ops console (bookmakers, feed health, overrides, jobs)', owner: 'Ops + Backend/Data', effort: '~24-36 hours' },
  { id: '11.5d', focus: 'Growth console (KPIs, funnels, cohorts, affiliates) - analytics foundation complete', owner: 'Growth + Data', effort: '~16-24 hours' },
  { id: '11.5e', focus: 'Security and compliance hardening', owner: 'Security/Platform', effort: '~12-18 hours' },
  { id: '11.5f', focus: 'Reporting and exports (CSV, saved views)', owner: 'Data/Analytics', effort: '~12-16 hours' },
]

const ADMIN_NAV_ITEMS: string[] = [
  'Overview',
  'Users & Orgs',
  'Billing',
  'Support',
  'Ops (Bookmakers, Odds Feed, Market Overrides, Jobs, Feature Flags, Cache Controls)',
  'Growth (KPIs, Funnels, Cohorts, Affiliates, Experiments, Pricing Gates)',
  'Security (Roles, Sessions, IP Access)',
  'Audit Log',
  'System Health',
  'Settings',
]

const ADMIN_PAGE_SPECS: AdminSpecSection[] = [
  {
    title: 'Overview',
    items: [
      'KPIs: MRR/ARR, churn, activation, DAU/WAU/MAU',
      'Health: odds feed latency, failed jobs, error rate',
      'Actions: maintenance mode toggle, announcement banner',
    ],
  },
  {
    title: 'Users & Orgs',
    items: [
      'Lookup: email/user/org, status, roles, plan',
      'Timeline: logins, key actions, alerts',
      'Actions: impersonate, revoke sessions, export/delete data',
    ],
  },
  {
    title: 'Billing',
    items: [
      'Plans, invoices, payment status, dunning',
      'Actions: change plan, comp, refund, resend invoice',
    ],
  },
  {
    title: 'Support',
    items: [
      'Notes/tags, internal flags (VIP, at-risk, abuse)',
      'Actions: GDPR export/delete, escalate, mark resolved',
    ],
  },
  {
    title: 'Bookmakers',
    items: [
      'List, region availability, affiliate IDs, priority order',
      'Actions: enable/disable, reorder, link health check',
    ],
  },
  {
    title: 'Odds Feed Health',
    items: [
      'Source latency, missing markets, stale leagues',
      'Actions: force refresh, rerun ingestion, suppress alerts',
    ],
  },
  {
    title: 'Market Overrides',
    items: [
      'Locks/voids/odds freeze with expiry + reason',
      'Actions: create override, add audit note',
    ],
  },
  {
    title: 'Jobs & Scheduler',
    items: [
      'Job status, last run, failures, retries',
      'Actions: retry, pause, disable job',
    ],
  },
  {
    title: 'Growth Analytics',
    items: [
      'Funnels, cohorts, retention, activation',
      'Powered by in-house analytics events pipeline (already implemented)',
      'Actions: export CSV, save report',
    ],
  },
  {
    title: 'Affiliate Performance',
    items: [
      'Clicks, conversions, revenue by bookmaker/region',
      'Actions: campaign tagging, attribution checks',
    ],
  },
  {
    title: 'Experiments',
    items: [
      'Experiment list, rollout %, guardrails',
      'Actions: ramp, pause, rollback',
    ],
  },
  {
    title: 'Security & Audit',
    items: [
      'RBAC, 2FA enforcement, IP allowlist',
      'Audit log with filters + export',
    ],
  },
  {
    title: 'System Health',
    items: [
      'Uptime, errors, queue depth, data freshness',
      'Actions: incident log, maintenance mode',
    ],
  },
]

// Sources
const SOURCES: Source[] = [
  {
    category: 'OddAlerts',
    links: [
      { name: 'OddAlerts Homepage', url: 'https://oddalerts.com/' },
      { name: 'OddAlerts Pro Features', url: 'https://oddalerts.com/pro', note: 'Filtering, alerts, betslip generator' },
      { name: 'In-Play Funnel', url: 'https://oddalerts.com/funnel', note: 'Live value alerts' },
      { name: 'AI Football Predictions', url: 'https://oddalerts.com/ai-football-predictions', note: 'ML-powered predictions' },
    ],
  },
  {
    category: 'FotMob',
    links: [
      { name: 'FotMob Website', url: 'https://www.fotmob.com/' },
      { name: 'FotMob iOS App Store', url: 'https://apps.apple.com/us/app/fotmob-soccer-live-scores/id488575683', note: '20M+ users' },
      { name: 'FotMob Google Play', url: 'https://play.google.com/store/apps/details?id=com.mobilefootie.wc2010&hl=en_US', note: '500+ leagues' },
    ],
  },
  {
    category: 'Betting Industry',
    links: [
      { name: 'RotoGrinders — Best Apps', url: 'https://rotogrinders.com/sports-betting/guides/best-apps-for-bettors' },
      { name: 'CBS Sports — Best Betting Apps', url: 'https://www.cbssports.com/betting/news/best-betting-apps/' },
      { name: 'SI — Prop Betting Sites', url: 'https://www.si.com/betting/usa/prop-betting-sites' },
      { name: 'FOX Sports — Live Betting', url: 'https://www.foxsports.com/stories/betting/live-betting' },
    ],
  },
  {
    category: 'Social Betting',
    links: [
      { name: 'BetsWall', url: 'https://apps.apple.com/us/app/betswall-football-betting-tips/id1064425227', note: '5M users, leaderboards' },
      { name: 'BettingPros', url: 'https://apps.apple.com/us/app/bettingpros-sports-betting/id1468109182', note: 'Follow experts' },
      { name: 'Gaming Today — Social Sportsbooks', url: 'https://www.gamingtoday.com/social-sportsbooks/' },
    ],
  },
  {
    category: 'xG & Data Visualization',
    links: [
      { name: 'Understat', url: 'https://understat.com/', note: 'xG stats for top 5 leagues' },
      { name: 'xG Stat', url: 'https://www.xgstat.com/', note: 'Shot maps, timelines' },
      { name: 'Football xG', url: 'https://footballxg.com/', note: '50 leagues' },
      { name: 'Odds Portal — Dropping Odds', url: 'https://www.oddsportal.com/dropping-odds/' },
    ],
  },
]

const PHASES: Phase[] = [
  {
    id: 'phase-1',
    number: '1',
    title: 'Core Product',
    status: 'complete',
    goal: 'Ship something genuinely useful and differentiated without odds or alerts.',
    features: [
      { name: 'Fixtures (EPL + Championship)', completed: true },
      { name: 'Fixture detail page', completed: true },
      { name: 'Team stats (Season / L5 / L3)', completed: true },
      { name: 'Player stats (Season / L5 / L3)', completed: true },
      { name: 'Predicted XI', completed: true },
      { name: 'Confirmed XI ingestion', completed: true },
      { name: 'Bet Builder tab (stats-driven)', completed: true },
      { name: 'User authentication', completed: true },
      { name: 'Live match updates', completed: true },
      { name: 'Home/away stat splits', completed: true },
      { name: 'Position-aware player props', completed: true },
      { name: 'Sample-size warnings', completed: true },
      { name: 'Builder correlation warnings', completed: true },
    ],
    dependencies: ['API-Football ingestion', 'DB schema & rollups', 'Market catalog', 'Mobile-first UI'],
    whyNow: ['Zero odds cost', 'Zero legal complexity', 'Strong differentiation vs OddAlerts', 'Users already get value'],
  },
  {
    id: 'phase-1.5',
    number: '1.5',
    title: 'Polish & Quick Wins',
    status: 'complete',
    goal: 'Address gaps and add high-impact, low-effort features before monetisation.',
    features: [
      { name: 'Correlation warnings in builder UI', completed: true },
      { name: 'Head-to-head stats', completed: true },
      { name: 'Recent form visualization (W/D/L)', completed: true },
      { name: 'Share/copy builder summary', completed: true },
      { name: 'Theme customization', completed: true },
      { name: 'Extended form windows (L10)', completed: true },
      { name: 'Referee data on fixtures', completed: true },
      { name: 'Player comparison tool', completed: true },
      { name: 'Automated scheduler for daily updates', completed: true },
    ],
    whyNow: ['Users expect head-to-head data', 'Form visualization aids quick decisions', 'Low effort, high value', 'Referee data essential for cards betting'],
  },
  {
    id: 'phase-2',
    number: '2',
    title: 'Monetisation-Ready UX',
    status: 'current',
    goal: 'Reduce friction and introduce revenue without changing product identity.',
    features: [
      { name: '"Open in bookmaker" buttons', completed: false },
      { name: 'Affiliate deep links', completed: false },
      { name: 'Preferred bookmaker setting', completed: true },
      { name: 'Builder summary / copyable legs', completed: true },
      { name: 'Market availability tags', completed: false },
      { name: 'PWA support', completed: false },
    ],
    dependencies: ['Phase 1.5 complete', 'Stable fixture & builder UI'],
    whyNow: ['Monetisation before odds', 'Improves UX immediately', 'No extra infra load'],
  },
  {
    id: 'phase-3',
    number: '3',
    title: 'Lightweight Odds Overlay',
    status: 'complete',
    goal: 'Add odds context, not odds dependence.',
    features: [
      { name: 'Odds ingestion (327 markets, 30+ bookmakers)', completed: true },
      { name: 'Hourly refresh + pre-kickoff burst', completed: true },
      { name: 'Odds shown as reference', completed: true },
      { name: 'Best odds highlighting', completed: true },
      { name: 'User preferred bookmaker selection', completed: true },
      { name: 'Odds-free toggle (hide odds in settings)', completed: true },
      { name: '"Add to betslip" deep links', completed: false },
    ],
    dependencies: ['Phase 2 in progress', 'Odds API selected', 'Credit usage strategy'],
    exclusions: ['No EV labels', 'No "value bet" claims', 'No alerts'],
  },
  {
    id: 'phase-3.5',
    number: '3.5',
    title: 'Probability System Upgrade',
    status: 'complete',
    goal: 'Comprehensive upgrade of probability engine against industry best practices, centered on Dixon-Coles.',
    features: [
      // Prerequisites (Week 1 - COMPLETE)
      { name: 'DixonColesParams table + migration', completed: true, note: 'Parameter storage' },
      { name: 'PredictionRecord table + migration', completed: true, note: 'For calibration' },
      { name: 'Data freshness gate', completed: true, note: 'Triggers recovery if stale' },
      { name: 'USE_DIXON_COLES feature flag', completed: true, note: 'Rollback capability' },
      { name: 'Promoted team detection + scaling', completed: true, note: 'Bayesian shrinkage' },
      // Week 2: Core Model (COMPLETE)
      { name: 'Dixon-Coles MLE fitting', completed: true, note: 'scipy.optimize L-BFGS-B' },
      { name: 'Tau correction for low-score correlation', completed: true, note: '0-0, 1-0, 0-1, 1-1' },
      { name: 'Weekly training scheduler job', completed: true, note: 'Sunday 03:00 UTC + recovery' },
      // Week 3: Integration & Caching (COMPLETE)
      { name: 'Replace Poisson 1X2 with Dixon-Coles', completed: true, note: 'Behind feature flag' },
      { name: 'Derive O/U, BTTS, correct score', completed: true, note: '42 markets use Dixon-Coles' },
      { name: 'Team Goals Range from correct scores', completed: true, note: 'Home/Away ranges' },
      { name: 'Builder response caching', completed: true, note: 'FixtureBuilderCache table, 4hr TTL' },
      { name: 'Cache invalidation before rollups', completed: true, note: 'Auto-clear on team/referee updates' },
      { name: 'Cache management CLI', completed: true, note: 'cache-stats, cache-clear commands' },
      // Week 4: Validation & Calibration (COMPLETE)
      { name: 'Brier score calculation', completed: true, note: 'calibration.py service' },
      { name: 'Log loss & calibration error metrics', completed: true },
      { name: 'Calibration report CLI', completed: true, note: 'By market/confidence/line' },
      { name: 'Log predictions before matches', completed: true, note: 'Scheduler 06:45 UTC' },
      { name: 'Resolve predictions post-match', completed: true, note: 'Scheduler 06:50 UTC' },
      // Statistical Improvements (COMPLETE)
      { name: 'Time-decay weighting', completed: true, note: 'ξ=0.02, half-life ~35 days' },
      { name: 'Bayesian shrinkage for promoted teams', completed: true, note: '0.5 credibility factor' },
      { name: 'Expand Poisson matrix to 11×11', completed: true },
      // Week 5+ Refinements
      { name: 'Dynamic home advantage by league', completed: true, note: 'Computed per league in LeagueAverages' },
      { name: 'Variance analysis (Poisson vs neg. binomial)', completed: true, note: 'Cards α=0.3, corners α=0.15' },
      { name: 'Use actual correlation matrix', completed: true, note: 'BTTS×O/U ρ=0.53, Result×BTTS correlations' },
      // Player Props (COMPLETE)
      { name: 'Improved minutes projection', completed: true, note: 'calculate_expected_minutes() with role-based projections' },
      { name: 'Starter vs sub conditional probabilities', completed: true, note: 'shots_per_start/sub, sot_per_start/sub' },
      { name: 'Role classification (starter/rotation/sub/fringe)', completed: true, note: 'Based on start_rate' },
      { name: 'Bayesian start probability', completed: true, note: 'Blends recent form with season prior' },
      // Season Transition (COMPLETE)
      { name: 'Auto-detect prior season', completed: true, note: 'find_prior_season()' },
      { name: 'Season credibility weighting', completed: true, note: '0.7→0.1 as fixtures accumulate' },
      { name: 'Promoted/relegated team detection', completed: true, note: 'League tier system' },
      { name: 'Parameter scaling for promotions', completed: true, note: '0.5 promoted, 0.7 relegated credibility' },
      // Dixon-Coles Markets (COMPLETE)
      { name: 'Clean sheet probabilities (home/away)', completed: true, note: 'Uses DC correct score matrix' },
      { name: 'Win to nil probabilities (home/away)', completed: true, note: 'Win + clean sheet combined' },
      { name: 'Team to score first (3-way)', completed: true, note: 'Home/Away/No Goal' },
      { name: 'First half goals over/under', completed: true, note: 'O/U 0.5, 1.5, 2.5 lines' },
      { name: 'First half BTTS (yes/no)', completed: true, note: 'Scaled from FT xG' },
      { name: 'Second half goals over/under', completed: true, note: 'O/U 0.5, 1.5, 2.5 lines' },
      { name: 'Second half BTTS (yes/no)', completed: true, note: 'Scaled from FT xG' },
      { name: 'HT/FT result probabilities (9-way)', completed: true, note: 'HH, HD, HA, DH, DD, DA, AH, AD, AA' },
      // Remaining Items (Deferred)
      { name: 'Historical backtesting suite', completed: true, note: 'backtest.py with walk-forward validation' },
      { name: 'A/B testing infrastructure', completed: false, note: 'Deferred to Phase 3.8 (requires CLV)' },
      { name: 'Dynamic home advantage by team', completed: true, note: 'calculate_team_home_advantage() with Bayesian shrinkage' },
      { name: 'Market-specific opponent factor clamps', completed: true, note: 'OPPONENT_FACTOR_CLAMPS dict' },
      { name: 'Position/role-aware player adjustments', completed: true, note: 'POSITION_MULTIPLIERS, apply_position_adjustment()' },
    ],
    dependencies: ['Historical fixture data (2 seasons)', 'Team stats & rollups', 'Poisson foundation', 'Startup recovery system', 'Catch-up scheduler job'],
    exclusions: ['No "value bet" labels', 'No automated betting recommendations', 'No EV calculations shown'],
    whyNow: ['Current Poisson model is basic — Dixon-Coles is industry standard', 'No validation framework to measure accuracy', 'Sample size penalties are arbitrary', 'Time-decay is low-effort, high-impact'],
  },
  {
    id: 'phase-3.5.5',
    number: '3.5.5',
    title: 'Referee Factor Integration',
    status: 'complete',
    goal: 'Apply referee statistics to all card/foul markets for improved probability accuracy.',
    features: [
      // Frontend Display (Complete)
      { name: 'AngleCard tooltip shows ref_factor breakdown', completed: true },
      { name: 'FixtureContextBanner shows referee strictness', completed: true },
      // Team Fouls
      { name: 'Apply referee foul factor to team fouls', completed: true, note: 'Uses calculate_referee_foul_factor()' },
      { name: 'Add ref_foul_factor to support field', completed: true },
      // Match Fouls
      { name: 'Apply referee foul factor to total match fouls', completed: true },
      // Player Cards
      { name: 'Apply referee card factor to player cards', completed: true, note: 'Uses calculate_combined_card_factor()' },
      { name: 'Apply derby/relegation uplift to player cards', completed: true, note: 'Included in combined_card_factor' },
      { name: 'Apply to "Score or Card" combination', completed: true, note: 'Uses combined_card_factor' },
      // Player Fouls
      { name: 'Apply foul factor to player fouls drawn', completed: true },
      { name: 'Apply foul factor to player fouls committed', completed: true },
      // Frontend Update
      { name: 'Add ref_foul_factor display to tooltip', completed: true, note: 'Shows in AngleCard tooltip' },
      { name: 'Add combined_card_factor display to tooltip', completed: true, note: 'Shows in AngleCard tooltip' },
    ],
    dependencies: ['Referee statistics system complete', 'probability.py factor functions', 'Referee rollups computed'],
    whyNow: ['Key gap: calculate_referee_foul_factor() never called', 'Foul markets ignore referee tendencies', 'Player card markets missing adjustments', 'High impact on card/foul market accuracy'],
  },
  {
    id: 'phase-3.5.6',
    number: '3.5.6',
    title: 'Probability Foundation Improvements',
    status: 'complete',
    goal: 'Fix known issues in probability calculations and implement proper statistical modeling.',
    features: [
      // Phase 3.5.6a: Cards Opponent Factor (COMPLETE)
      { name: 'Fix cards opponent factor bug', completed: true, note: 'Was using fouls_drawn instead of cards_caused' },
      { name: 'Add cards_y_against to rollups', completed: true, note: 'Tracks opponent cards caused' },
      { name: 'Update fixtures.py to use opp_cards_caused', completed: true },
      { name: 'Regenerate rollups with cards_y_against', completed: true, note: '2025 season populated' },
      // Phase 3.5.6b: Dynamic League Averages (COMPLETE)
      { name: 'Create LeagueAverages model', completed: true, note: 'league_id, season_id, stat_type, avg' },
      { name: 'Implement league averages aggregation job', completed: true, note: 'compute_league_averages()' },
      { name: 'Add CLI command compute-league-avgs', completed: true, note: 'python -m app.cli compute-league-avgs' },
      { name: 'Update fixtures.py for dynamic lookup', completed: true, note: 'get_league_averages()' },
      { name: 'Populate league averages data', completed: true, note: '2025 season: corners 4.88, shots 12.1, etc.' },
      // Phase 3.5.6c: Interaction Modeling (COMPLETE)
      { name: 'Interaction model for corners', completed: true, note: '(team_for + opp_against) / 2' },
      { name: 'Interaction model for shots/SOT', completed: true },
      { name: 'Interaction model for fouls', completed: true },
      // Phase 3.5.6d: Distribution Calibration (COMPLETE)
      { name: 'Backtesting infrastructure', completed: true, note: 'CLI: backtest-predictions, 60.8% 1X2, 63.8% O/U accuracy' },
      { name: 'Calibration factors per market', completed: true, note: 'CLI: calculate-calibration-factors, per-band adjustments' },
      { name: 'Calibration factor functions', completed: true, note: 'calculate_calibration_factors_from_backtest(), apply_calibration_factor()' },
    ],
    dependencies: ['Rollups infrastructure', 'Fixture stats', 'probability.py foundations'],
    whyNow: ['Cards using wrong opponent factor', 'Hardcoded league averages', 'Naive opponent modeling reduces accuracy', 'Foundation for calibration framework'],
  },
  {
    id: 'phase-3.7',
    number: '3.7',
    title: 'Value Betting Display',
    status: 'complete',
    goal: 'Show the value each bet offers by comparing our computed probability to bookmaker implied probability.',
    features: [
      { name: 'Edge calculation utility', completed: true, note: 'calculateValueDisplay() in odds.ts' },
      { name: 'Edge badge display next to odds', completed: true, note: 'Green = value, red = negative edge' },
      { name: 'Show edge for preferred + best odds', completed: true },
      { name: 'User-configurable minimum edge threshold', completed: true, note: 'Settings: 0%, 3%, 5%, 10%' },
      { name: 'Edge display toggle in settings', completed: true, note: 'Show/hide value badges' },
    ],
    dependencies: ['Odds integration (Phase 3)', 'Probability system (Phase 3.5)'],
    whyNow: ['Users see prob + odds but must calculate edge manually', 'No backend changes required', 'High user value, low complexity', 'Natural extension of odds overlay'],
  },
  {
    id: 'phase-3.9',
    number: '3.9',
    title: 'Value Bets Aggregator',
    status: 'complete',
    goal: 'Aggregate all value betting opportunities across fixtures into a single filterable page.',
    features: [
      // Backend
      { name: 'New /fixtures/value-bets endpoint', completed: true },
      { name: 'Aggregation service across fixtures', completed: true },
      { name: 'Server-side filtering (odds, edge, market, league)', completed: true },
      { name: 'Pagination support', completed: true },
      { name: 'Caching layer with 15-min TTL', completed: true },
      { name: 'Scheduler job for cache refresh', completed: true },
      // Frontend
      { name: 'Value Bets page', completed: true },
      { name: 'Filter bar (categories, leagues, ranges)', completed: true },
      { name: 'Compact value bet cards with expand', completed: true },
      { name: 'Calculation breakdown with support data', completed: true },
      { name: 'Sort by edge, odds, conviction, kickoff', completed: true },
      { name: 'Navigation link in BottomNav', completed: true },
      { name: 'Add to builder integration', completed: true },
      { name: 'User custom odds input with edge calc', completed: true },
      { name: 'All bookmaker odds comparison', completed: true },
    ],
    dependencies: ['Odds integration (Phase 3)', 'Value display system (Phase 3.7)', 'Builder angles with conviction %'],
    whyNow: ['Users see value badges per-fixture — surface best opportunities', 'Natural extension of Phase 3.7', 'High user value, moderate complexity'],
  },
  {
    id: 'phase-3.8',
    number: '3.8',
    title: 'CLV Tracking (Admin-Only)',
    status: 'complete',
    goal: 'Measure model quality against Pinnacle closing lines. CLV is the gold standard for evaluating prediction models.',
    features: [
      // Infrastructure
      { name: 'Add Pinnacle to bookmakers table', completed: true, note: 'api_bookmaker_id=4' },
      { name: 'Extend PredictionRecord with CLV columns', completed: true, note: 'pinnacle_closing_odds, pinnacle_implied_prob, clv' },
      { name: 'Create closing odds capture service', completed: true, note: 'closing_odds.py' },
      { name: 'Map prediction outcomes to odds queries', completed: true, note: '1X2, BTTS, O/U mappings' },
      // Closing Odds Capture
      { name: 'Scheduler job for closing odds', completed: true, note: 'Capture 5 mins before kickoff' },
      { name: 'Refresh Pinnacle odds specifically', completed: true, note: 'API filter for fresh data' },
      { name: 'Calculate CLV for predictions', completed: true, note: 'our_prob - pinnacle_implied' },
      // Admin Access
      { name: 'Create get_current_admin_user()', completed: false, note: 'Deferred to Phase 11 (SaaS Launch)' },
      { name: 'Secure existing admin endpoints', completed: true },
      { name: 'CLV data restricted to admins', completed: true },
      // Reporting
      { name: 'CLV statistics service', completed: true, note: 'avg, median, positive %, by market' },
      { name: 'Admin-only CLV API endpoint', completed: true, note: '/v1/admin/clv-report' },
      { name: 'CLV CLI command', completed: true, note: 'clv-report' },
    ],
    dependencies: ['Odds ingestion (Phase 3)', 'Prediction logging (Phase 3.5)', 'PredictionRecord table'],
    exclusions: ['No CLV data shown to users', 'No sharp line labels in UI', 'No automated betting'],
    whyNow: ['CLV is the only reliable long-term model quality measure', 'Validates Dixon-Coles implementation', 'Admin-only — no legal concerns', 'Leverages existing infrastructure'],
  },
  {
    id: 'phase-4',
    number: '4',
    title: 'Power User Retention',
    status: 'complete',
    goal: 'Increase session depth and daily return rate.',
    features: [
      { name: 'Saved bet builders (backend)', completed: true, note: 'Backend + API complete' },
      { name: 'Player shortlists', completed: true, note: 'Fully working' },
      { name: 'Manual bet tracker (backend)', completed: true, note: 'Fully working' },
      { name: 'Private fixture notes', completed: true, note: 'Fully working' },
      { name: 'Bet history & statistics', completed: true, note: 'Fully working' },
      { name: 'Export to CSV', completed: true, note: 'Fully working' },
    ],
    whyNow: ['Retention > acquisition', 'These features are sticky', 'Low external dependency'],
  },
  {
    id: 'phase-5',
    number: '5',
    title: 'Event-Level Expansion',
    status: 'complete',
    goal: 'Unlock first-half / each-half markets properly.',
    features: [
      { name: 'Time-split team stats', completed: true, note: 'TeamTimeSplitRollup model, compute-time-splits CLI' },
      { name: 'Time-split player stats', completed: true, note: 'PlayerTimeSplitRollup model' },
      { name: 'First-half / each-half builder markets', completed: true, note: 'v2 probability functions use team-specific ratios' },
      { name: 'Tempo profiling', completed: true, note: 'TeamTempoProfile, TempoBadge, TempoChart components' },
      { name: 'Time-splits API endpoint', completed: true, note: '/fixtures/{id}/time-splits with period breakdown' },
      { name: 'TimeSplitStatsCard component', completed: true, note: 'Goal distribution by half visualization' },
      { name: 'TempoChart component', completed: true, note: 'Scored/Conceded toggle, 15-min period bars' },
      { name: 'TeamsTab time-splits view', completed: true, note: 'New view mode in TeamsTab' },
    ],
    dependencies: ['Event ingestion pipeline ✅', 'Historical events backfill ✅'],
    whyNow: ['Replaces hardcoded 0.43 ratio with team-specific data', 'Improves HT/FT market accuracy', 'Visual tempo profiles for user insight'],
  },
  {
    id: 'phase-6',
    number: '6',
    title: 'Alerts & Live Intelligence',
    status: 'complete',
    goal: 'Add alerts without turning into OddAlerts.',
    features: [
      // Phase 6a: Infrastructure (COMPLETE)
      { name: 'AlertPreference model', completed: true, note: 'User alert settings' },
      { name: 'UserAlert model', completed: true, note: 'Alert history storage' },
      { name: 'Alert preferences API', completed: true, note: 'GET/PUT /alerts/preferences' },
      { name: 'Alert history API', completed: true, note: 'GET /alerts, dismiss endpoint' },
      // Phase 6b: Lineup alerts (COMPLETE)
      { name: 'Lineup confirmation alerts', completed: true, note: 'generate_lineup_alerts() in scheduler' },
      { name: 'WebSocket lineup broadcast', completed: true, note: '/ws/alerts/{user_id} endpoint' },
      { name: 'Frontend toast notifications', completed: true, note: 'react-hot-toast + useAlerts hook' },
      // Phase 6c: Builder invalidation (COMPLETE)
      { name: 'Builder invalidation detection', completed: true, note: 'check_builder_invalidation()' },
      { name: 'WebSocket invalidation broadcast', completed: true, note: 'broadcast_alert()' },
      { name: 'Warning badge on saved builders', completed: true, note: '/user/builders/with-status endpoint + UI badge' },
      // Phase 6d: Foul feed (COMPLETE)
      { name: 'Live foul feed with booking warnings', completed: true, note: 'check_booking_warnings()' },
      { name: 'WebSocket booking warning', completed: true, note: 'broadcast_booking_warning()' },
      { name: 'BookingWarningBanner component', completed: true, note: 'With risk badges' },
      // Phase 6e: Quick-bet links (COMPLETE)
      { name: 'Bookmaker links service', completed: true, note: '10 UK bookmakers configured' },
      { name: 'Bookmaker link API endpoints', completed: true, note: '/bookmakers, /bookmaker-link/{slug}' },
      { name: 'Click-through tracking', completed: true, note: 'Logging active, DB persistence TODO' },
      { name: 'BookmakerLinksButton component', completed: true, note: 'Dropdown in bet builder' },
      // Phase 6f: Push (COMPLETE)
      { name: 'Push notification service', completed: true, note: 'VAPID auth via pywebpush' },
      { name: 'Push subscription endpoint', completed: true, note: '/subscribe, /vapid-public-key' },
      { name: 'Alert-triggered push dispatch', completed: true, note: 'Auto-sends on alert creation' },
      { name: 'Service worker (sw.js)', completed: true, note: 'With push handlers' },
      { name: 'PWA manifest', completed: true, note: 'Icons, shortcuts, installable' },
      { name: 'usePushNotifications hook', completed: true, note: 'Subscription management' },
      { name: 'Push settings in SettingsModal', completed: true, note: 'Toggle + alert types' },
      // Already done
      { name: 'Player availability filtering', completed: true, note: 'Phase 6.5 complete' },
    ],
    dependencies: ['User preferences (Phase 2)', 'Saved builders (Phase 4)', 'Player availability (Phase 6.5)', 'WebSocket infrastructure (Phase 12)'],
    exclusions: ['Live odds alerts', 'In-play odds scanning', 'Daily digest emails', 'Automated betting'],
    whyNow: ['All dependencies complete', 'WebSocket infrastructure ready', 'Lineup scheduler already runs every 15 mins', 'High user value with moderate complexity'],
  },
  {
    id: 'phase-6.5',
    number: '6.5',
    title: 'Player Availability Filtering',
    status: 'complete',
    goal: 'Automatically filter out player-specific markets when a player is unavailable (injured, suspended, sidelined).',
    features: [
      { name: 'PlayerSidelined + PlayerAvailability tables', completed: true, note: 'Severity classification' },
      { name: 'League-level injury ingestion', completed: true, note: 'API-Football /injuries endpoint (batch)' },
      { name: 'Scheduler job (every 2 hours)', completed: true, note: 'run_ingest_league_injuries' },
      { name: 'Lineup check (every 15 mins)', completed: true, note: 'run_check_lineups' },
      { name: 'Filter player markets in builder', completed: true, note: 'should_filter_from_builder()' },
      { name: 'AvailabilityBadge component', completed: true, note: 'INJ, DTF, SUS, OUT badges' },
      { name: 'Doubtful players kept until confirmed', completed: true },
      { name: 'Live tips integration', completed: false, note: 'Phase 9 prerequisite' },
      { name: 'Substitution cross-reference', completed: false, note: 'Phase 9 prerequisite' },
    ],
    dependencies: ['Player stats infrastructure', 'Scheduler infrastructure (APScheduler)', 'API-Football /injuries endpoint'],
    whyNow: ['Critical for user trust', 'Prerequisite for Live Tips (Phase 9)', 'Prevents wasted user research time'],
  },
  {
    id: 'phase-7',
    number: '7',
    title: 'Multi-League Expansion',
    status: 'complete',
    goal: 'Scale breadth without losing quality.',
    features: [
      { name: 'New leagues (6 active)', completed: true, note: 'EPL, Championship, La Liga, Bundesliga, Serie A, Ligue 1' },
      { name: 'League switching UI', completed: true },
      { name: 'User-controlled league refresh', completed: true, note: 'Refresh button with spinner' },
      { name: 'League-specific stat tuning', completed: true, note: 'LeagueAverages per league' },
    ],
    dependencies: ['Proven EPL engagement', 'Odds cost model validated'],
  },
  {
    id: 'phase-8',
    number: '8',
    title: 'AI & Predictions',
    status: 'current',
    goal: 'Build a transparent, data-driven insight system that detects patterns and anomalies without making predictions.',
    features: [
      // Phase 8a: Core Infrastructure
      { name: 'Database models (DetectedPattern, DetectedAnomaly, FixtureInsight)', completed: false, note: 'Phase 8a' },
      { name: 'Alembic migration for insights tables', completed: false, note: 'Phase 8a' },
      { name: 'Pattern type enums (late_goals, corner_surge, home_away_divergence)', completed: false, note: 'Phase 8a' },
      { name: 'Anomaly type enums (stat_spike, stat_drop)', completed: false, note: 'Phase 8a' },
      // Phase 8b: Pattern Detection
      { name: 'Team pattern detector (late goals, venue splits, corner surge)', completed: false, note: 'Phase 8b' },
      { name: 'Player pattern detector (hot streak, card magnet)', completed: false, note: 'Phase 8b' },
      { name: 'H2H pattern detector (dominance, stat patterns)', completed: false, note: 'Phase 8b' },
      { name: 'Anomaly detector (z-score based deviation)', completed: false, note: 'Phase 8b' },
      // Phase 8c: NLG Templates
      { name: 'Template-based natural language generation', completed: false, note: 'Phase 8c' },
      { name: 'Pattern headline templates', completed: false, note: 'Phase 8c' },
      { name: 'Evidence summary templates', completed: false, note: 'Phase 8c' },
      // Phase 8d: API & Aggregation
      { name: 'InsightAggregator service', completed: false, note: 'Phase 8d' },
      { name: '/insights/fixture/{id} endpoint', completed: false, note: 'Phase 8d' },
      { name: '/patterns/team/{id} endpoint', completed: false, note: 'Phase 8d' },
      { name: 'Response schemas (FixtureInsightsResponse)', completed: false, note: 'Phase 8d' },
      // Phase 8e: Frontend Integration
      { name: 'InsightCard component (expandable evidence)', completed: false, note: 'Phase 8e' },
      { name: 'InsightsSummary component', completed: false, note: 'Phase 8e' },
      { name: 'PatternBadge component (inline indicator)', completed: false, note: 'Phase 8e' },
      { name: 'AnomalyBadge component (deviation indicator)', completed: false, note: 'Phase 8e' },
      { name: 'FixtureCard integration (expandable insights)', completed: false, note: 'Phase 8e' },
      { name: 'AngleCard tooltip integration (patterns)', completed: false, note: 'Phase 8e' },
      // Phase 8f: Scheduler & Caching
      { name: 'Weekly pattern detection job', completed: false, note: 'Phase 8f' },
      { name: 'Daily insight pre-computation', completed: false, note: 'Phase 8f' },
      { name: 'Cache invalidation on rollup updates', completed: false, note: 'Phase 8f' },
    ],
    dependencies: ['Proven user base ✅', 'Strong data foundation (Dixon-Coles, time-splits, H2H) ✅', 'Clear positioning that avoids "tips" ✅'],
    exclusions: ['Win probability percentages', '"Value bet" recommendations', 'Tipster-style predictions', 'Guaranteed returns messaging'],
    principles: ['AI explains patterns, doesn\'t predict outcomes', 'Always show underlying data', 'No confidence scores or "banker" labels', 'Transparency over black-box predictions'],
    whyNow: ['Market expects AI features', 'Differentiate by being transparent about methodology', 'Leverages existing statistical infrastructure'],
  },
  {
    id: 'phase-9',
    number: '9',
    title: 'Live Tips',
    status: 'complete',
    goal: 'Real-time in-play tips for high-probability betting lines that haven\'t yet landed.',
    features: [
      { name: 'Live Tips page with ticker feed', completed: true, note: 'LiveTipsPage.tsx with WebSocket' },
      { name: 'Live odds ingestion (1-2 min intervals)', completed: true, note: 'live_odds_ingestion.py' },
      { name: 'Tip generation engine', completed: true, note: 'live_tips_service.py with Dixon-Coles' },
      { name: 'Player on-pitch verification', completed: true, note: 'pitch_tracker.py + LivePlayerOnPitch' },
      { name: 'Time window filtering (HT to 70 mins)', completed: true, note: '20+ mins remaining' },
      { name: 'Tip card display', completed: true, note: 'LiveTipCard.tsx' },
      { name: 'Bookmaker odds comparison', completed: true, note: 'Best odds + bookmaker shown' },
      { name: 'Auto-refresh ticker', completed: true, note: 'WebSocket with reconnection' },
      { name: 'Market filtering', completed: true, note: 'LiveTipsFilterBar.tsx' },
      { name: 'Substitution detection', completed: true, note: 'Invalidates tips when subbed' },
      { name: 'Tip statistics display', completed: true, note: 'Active, landed, missed, accuracy %' },
      { name: 'REST API endpoints', completed: true, note: '/tips/live, /fixture/{id}, /history, /stats' },
    ],
    dependencies: ['Live odds ingestion', 'Live match events', 'Player on-pitch status', 'Pre-match probabilities (Dixon-Coles)', 'Player availability filtering (Phase 6.5)'],
    exclusions: ['No automated betting', 'No "value bet" or EV calculations', 'No tips with <20 mins remaining', 'No player tips when off pitch'],
    principles: ['Tips are probability-based, not guaranteed', 'Always show pre-match probability', 'No "banker" or "lock" language', 'Tips are informational — user decides'],
  },
  {
    id: 'phase-10',
    number: '10',
    title: 'Comprehensive Stats Extraction',
    status: 'current',
    goal: 'Extract all available API-Football stats from raw JSON into structured database columns for optimal query performance.',
    features: [
      // Phase 10a: Team Match Stats (COMPLETE)
      { name: 'Team match stats extraction', completed: true, note: 'xG, shots, passes, tackles, etc.' },
      { name: 'FixtureTeamStats new columns', completed: true, note: '13 new columns' },
      { name: 'Team stats backfill script', completed: true },
      { name: 'TeamRollup metrics update', completed: true },
      // Phase 10b: Player Match Stats (HIGH-PRIORITY COMPLETE)
      { name: 'Player match stats extraction', completed: true, note: 'Aerials, clearances extracted' },
      { name: 'FixturePlayerStats new columns', completed: true, note: 'aerials_total, aerials_won, clearances' },
      { name: 'Goalkeeper-specific columns', completed: false, note: 'Deferred (low priority)' },
      { name: 'PlayerRollup metrics update', completed: true, note: 'Aerial + ground duel metrics' },
      // Phase 10c: Team Season Stats
      { name: 'Team season stats expansion', completed: false, note: 'Deferred (avg goals, streaks)' },
      // Phase 10d: Rollup Enhancements (COMPLETE)
      { name: 'Extended rollup computations', completed: true, note: 'Ground duels, per-90s added' },
      // Phase 10e: API Response Updates (COMPLETE)
      { name: 'xG exposed in LiveStats API', completed: true, note: 'Shows in fixture cards' },
      { name: 'Aerial duel builder angles', completed: true, note: 'Defense & Duels category' },
      // Phase 10f: Backfill & Validation
      { name: 'Full backfill & validation', completed: false, note: 'Only needed for new columns' },
    ],
    dependencies: ['Raw JSON data stored in database', 'Existing ingestion pipeline', 'Rollup computation infrastructure'],
    whyNow: ['Enable SQL queries without JSON parsing', 'Support new markets (xG, aerials)', 'Improve query performance', 'Foundation for AI pattern detection'],
  },
  {
    id: 'phase-11',
    number: '11',
    title: 'SaaS Launch & Deployment',
    status: 'upcoming',
    goal: 'Launch paid subscriptions with production-grade infrastructure and compliance.',
    features: [
      { name: 'Managed auth provider integration (Clerk)', completed: false },
      { name: 'Stripe subscriptions + 7-day trial', completed: false },
      { name: 'Promo codes + Stripe Tax + invoice branding', completed: false },
      { name: 'Webhook-driven subscription gating', completed: false },
      { name: 'Pricing page + billing portal', completed: false },
      { name: 'GDPR data export + account deletion flows', completed: false },
      { name: 'Legal docs (Terms, Privacy, Cookie)', completed: false },
      { name: 'Cloudflare Pages frontend deploy', completed: false },
      { name: 'VPS backend + reverse proxy (Caddy/Nginx)', completed: false },
      { name: 'Neon autoscale 0.25-0.5 CU', completed: false },
      { name: 'Staging environment + rollback plan', completed: false },
      { name: 'Monitoring + alerting (Sentry + uptime)', completed: false },
      { name: 'CORS locked to www.statscout.co', completed: false },
    ],
    dependencies: ['Phase 2 complete', 'Stable core API + odds overlay'],
    whyNow: ['Paid access requires billing + auth', 'Production launch needs monitoring + rollback', 'Compliance before onboarding users'],
  },
  {
    id: 'phase-11.5',
    number: '11.5',
    title: 'Admin Area (Support/Ops/Growth)',
    status: 'upcoming',
    goal: 'Ship a comprehensive internal console that covers Support, Ops, and Growth workflows in one place.',
    features: [
      { name: 'In-house analytics events pipeline (backend + frontend)', completed: true, note: 'analytics_events + batching + sendBeacon' },
      { name: 'Landing page instrumentation (page views, scroll depth, CTA, sections)', completed: true },
      { name: 'User/org lookup (email, user ID, org ID)', completed: false },
      { name: 'Impersonation / login-as with audit trail', completed: false },
      { name: 'Subscription view (plan, invoices, dunning)', completed: false },
      { name: 'User timeline (logins, key actions, alerts)', completed: false },
      { name: 'Admin notes/tags + internal flags', completed: false },
      { name: 'GDPR export/delete workflows', completed: false },
      { name: 'Bookmaker management (enable/disable, regions, affiliates)', completed: false },
      { name: 'Odds feed health (latency, missing markets)', completed: false },
      { name: 'Market overrides (lock/void, odds freeze)', completed: false },
      { name: 'Background jobs dashboard', completed: false },
      { name: 'Feature flags + maintenance mode', completed: false },
      { name: 'Data freshness + cache invalidation controls', completed: false },
      { name: 'Core KPIs (MRR/ARR, churn, activation, DAU/WAU/MAU)', completed: false },
      { name: 'Funnel + cohort analysis (trial to paid)', completed: false },
      { name: 'Affiliate performance (clicks, conversion, revenue)', completed: false },
      { name: 'A/B testing and rollout controls', completed: false },
      { name: 'Plan and pricing experiments', completed: false },
      { name: 'Role-based access control (Support/Ops/Growth)', completed: false },
      { name: '2FA enforcement + session revocation', completed: false },
      { name: 'Admin audit log (immutable)', completed: false },
      { name: 'IP allowlist/denylist for admin access', completed: false },
    ],
    dependencies: [
      'Phase 11 launch essentials (auth, billing, legal)',
      'Admin-only API endpoints framework (Phase 3.8c)',
      'In-house analytics system (events pipeline)',
      'Monitoring infrastructure (Sentry + uptime)',
    ],
    whyNow: [
      'Running paid SaaS without admin tooling slows support and growth',
      'Ops controls are required to manage odds feed quality and bookmaker compliance',
      'Centralized analytics ensures decisions are based on real product data',
    ],
  },
  {
    id: 'phase-12',
    number: '12',
    title: 'Data Systems Infrastructure',
    status: 'complete',
    goal: 'Improve data freshness, error handling, and frontend-backend synchronization based on comprehensive systems review.',
    features: [
      // Phase 12a: Real-Time Updates (High Priority) - COMPLETE
      { name: 'WebSocket server for live matches', completed: true, note: 'FastAPI WebSocket + ConnectionManager' },
      { name: 'Live score/stats push to clients', completed: true, note: 'broadcast_score_update, broadcast_stats_update' },
      { name: 'WebSocket fallback to polling', completed: true, note: '30s polling when WS disconnected' },
      { name: 'Connection management (reconnect, heartbeat)', completed: true, note: 'Exponential backoff, ping/pong' },
      // Phase 12b: Frontend Data Fetching (High Priority) - COMPLETE
      { name: 'SWR integration', completed: true, note: 'swr-config.tsx with custom hooks' },
      { name: 'Stale-while-revalidate pattern', completed: true, note: 'keepPreviousData, revalidateIfStale' },
      { name: 'Auto-refetch on window focus', completed: true, note: 'revalidateOnFocus: true' },
      { name: 'Retry logic with exponential backoff', completed: true, note: '3 retries: 1s, 2s, 4s' },
      // Phase 12c: Error Handling & Monitoring (High Priority) - COMPLETE
      { name: 'Centralized error monitoring (Sentry)', completed: true, note: 'sentry.ts with capture utilities' },
      { name: 'Frontend error boundaries', completed: true, note: 'ErrorBoundary component + HOC wrapper' },
      { name: 'User-friendly error messages + retry', completed: true, note: 'Network/API/timeout detection' },
      { name: 'API rate limit queue with priorities', completed: true, note: 'Token bucket, 4-tier priorities' },
      // Phase 12d: Cache & Freshness (COMPLETE)
      { name: 'ETag/If-Modified-Since support', completed: true, note: 'CacheMiddleware with 304 responses' },
      { name: 'Data freshness indicator in UI', completed: true, note: 'FreshnessIndicator component' },
      { name: 'Frontend odds cache force-refresh', completed: true, note: 'forceRefreshOdds in OddsContext' },
      { name: 'Cache invalidation recovery handling', completed: true, note: 'cache_recovery.py + scheduler job' },
      // Phase 12e: Database Performance (COMPLETE)
      { name: 'Compound indexes (13 indexes)', completed: true, note: 'fixture_odds, fixtures, rollups, cache, h2h, dixon_coles' },
      { name: 'Connection pooling optimization', completed: true, note: 'pool_size=10, max_overflow=20, pool_recycle=1800s' },
      { name: 'Redis for hot endpoints', completed: false, note: 'Deferred - current caching sufficient' },
      { name: 'Query plan analysis CLI', completed: false, note: 'Optional diagnostic tool' },
      // Phase 12f: Minor Improvements (COMPLETE)
      { name: 'Standardize localStorage keys', completed: true, note: 'Already done with statscout_ prefix' },
      { name: 'Remove hardcoded auth delay', completed: true, note: 'waitForAuthReady promise' },
      { name: 'Loading skeletons for fetch states', completed: true, note: 'Skeleton.tsx components' },
      { name: 'Data version header for cache validation', completed: true, note: 'X-Data-Version header' },
    ],
    dependencies: ['Core data pipeline functional', 'Scheduler infrastructure', 'Builder cache system', 'Phase 11 monitoring (Sentry)'],
    whyNow: ['Users see delayed live scores without WebSocket', 'Single API failure breaks UI state', 'Silent errors confuse users', 'Cache gaps serve stale probabilities'],
  },
]

function PhaseCard({ phase, index, isVisible }: { phase: Phase; index: number; isVisible: boolean }) {
  const [isExpanded, setIsExpanded] = useState(phase.status === 'current')
  const isLeft = index % 2 === 0

  const completedCount = phase.features.filter(f => f.completed).length
  const totalCount = phase.features.length
  const progress = Math.round((completedCount / totalCount) * 100)

  const getStatusColor = () => {
    switch (phase.status) {
      case 'complete': return 'var(--green)'
      case 'current': return 'var(--accent)'
      default: return 'var(--muted)'
    }
  }

  const getStatusLabel = () => {
    switch (phase.status) {
      case 'complete': return 'Complete'
      case 'current': return 'In Progress'
      default: return 'Upcoming'
    }
  }

  return (
    <div
      className={`roadmap-phase ${isLeft ? 'left' : 'right'} ${isVisible ? 'visible' : ''}`}
      style={{ '--delay': `${index * 0.1}s` } as React.CSSProperties}
    >
      <div className="phase-connector">
        <div
          className={`phase-dot ${phase.status}`}
          style={{ borderColor: getStatusColor() }}
        >
          {phase.status === 'complete' ? (
            <IoCheckmarkCircle size={24} color="var(--green)" />
          ) : (
            <span className="phase-number">{phase.number}</span>
          )}
        </div>
      </div>

      <div className={`phase-card ${phase.status}`}>
        <div className="phase-header" onClick={() => setIsExpanded(!isExpanded)}>
          <div className="phase-title-group">
            <span className="phase-badge" style={{ background: getStatusColor() }}>
              Phase {phase.number}
            </span>
            <h3 className="phase-title">{phase.title}</h3>
            <span className="phase-status" style={{ color: getStatusColor() }}>
              {getStatusLabel()}
            </span>
          </div>
          <div className="phase-toggle">
            {isExpanded ? <IoChevronUp size={20} /> : <IoChevronDown size={20} />}
          </div>
        </div>

        {phase.status !== 'upcoming' && (
          <div className="phase-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%`, background: getStatusColor() }}
              />
            </div>
            <span className="progress-text">{completedCount}/{totalCount}</span>
          </div>
        )}

        <p className="phase-goal">{phase.goal}</p>

        <div className={`phase-content ${isExpanded ? 'expanded' : ''}`}>
          <div className="phase-features">
            <h4 className="section-label">Features</h4>
            <ul className="feature-list">
              {phase.features.map((feature, i) => (
                <li key={i} className={`feature-item ${feature.completed ? 'completed' : ''}`}>
                  {feature.completed ? (
                    <IoCheckmarkCircle size={16} color="var(--green)" />
                  ) : (
                    <IoEllipseOutline size={16} color="var(--muted)" />
                  )}
                  <span>{feature.name}</span>
                  {feature.note && <span className="feature-note">{feature.note}</span>}
                </li>
              ))}
            </ul>
          </div>

          {phase.dependencies && phase.dependencies.length > 0 && (
            <div className="phase-dependencies">
              <h4 className="section-label">Dependencies</h4>
              <div className="dependency-chips">
                {phase.dependencies.map((dep, i) => (
                  <span key={i} className="dependency-chip">{dep}</span>
                ))}
              </div>
            </div>
          )}

          {phase.exclusions && phase.exclusions.length > 0 && (
            <div className="phase-exclusions">
              <h4 className="section-label">Exclusions</h4>
              <div className="exclusion-list">
                {phase.exclusions.map((exc, i) => (
                  <span key={i} className="exclusion-item">{exc}</span>
                ))}
              </div>
            </div>
          )}

          {phase.principles && phase.principles.length > 0 && (
            <div className="phase-principles">
              <h4 className="section-label">Guiding Principles</h4>
              <ul className="principles-list">
                {phase.principles.map((principle, i) => (
                  <li key={i}>{principle}</li>
                ))}
              </ul>
            </div>
          )}

          {phase.whyNow && phase.whyNow.length > 0 && (
            <div className="phase-why">
              <h4 className="section-label">Why now?</h4>
              <ul className="why-list">
                {phase.whyNow.map((why, i) => (
                  <li key={i}>{why}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function RoadmapPage() {
  const [visiblePhases, setVisiblePhases] = useState<Set<number>>(new Set())
  const phaseRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target.getAttribute('data-index'))
          if (entry.isIntersecting) {
            setVisiblePhases((prev) => new Set([...prev, index]))
          }
        })
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    )

    phaseRefs.current.forEach((ref, index) => {
      if (ref) {
        ref.setAttribute('data-index', String(index))
        observer.observe(ref)
      }
    })

    return () => observer.disconnect()
  }, [])

  const completedPhases = PHASES.filter(p => p.status === 'complete').length
  const totalFeatures = PHASES.reduce((acc, p) => acc + p.features.length, 0)
  const completedFeatures = PHASES.reduce((acc, p) => acc + p.features.filter(f => f.completed).length, 0)
  const overallProgress = Math.round((completedFeatures / totalFeatures) * 100)

  return (
    <div className="roadmap-page">
      <div className="roadmap-header">
        <h1 className="roadmap-title">Product Roadmap</h1>
        <p className="roadmap-subtitle">Think in milestones, not features. Each phase unlocks the next.</p>

        <div className="roadmap-stats">
          <div className="stat-item">
            <span className="stat-value">{completedPhases}/{PHASES.length}</span>
            <span className="stat-label">Phases</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{completedFeatures}/{totalFeatures}</span>
            <span className="stat-label">Features</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{overallProgress}%</span>
            <span className="stat-label">Complete</span>
          </div>
        </div>
      </div>

      <div className="roadmap-timeline">
        <div className="timeline-line" />

        {PHASES.map((phase, index) => (
          <div
            key={phase.id}
            ref={(el) => { phaseRefs.current[index] = el }}
            className="phase-wrapper"
          >
            <PhaseCard
              phase={phase}
              index={index}
              isVisible={visiblePhases.has(index)}
            />
          </div>
        ))}
      </div>

      <div className="roadmap-footer">
        <div className="footer-card">
          <h3>Strategic Summary</h3>
          <ul className="summary-list">
            <li><IoCheckmarkCircle size={16} color="var(--green)" /> Delivery order is correct</li>
            <li><IoCheckmarkCircle size={16} color="var(--green)" /> Feature discipline is strong</li>
            <li><IoCheckmarkCircle size={16} color="var(--green)" /> Single paid tier fits perfectly</li>
          </ul>
          <p className="growth-note">
            <strong>statscout grows by:</strong> Trust, Clarity, Low friction — not hype.
          </p>
        </div>
      </div>

      {/* Admin Area Plan Section */}
      <div className="roadmap-section">
        <h2 className="section-title">Admin Area Plan (Phase 11.5)</h2>
        <p className="section-subtitle">Milestones, navigation map, and page-level specs for Support, Ops, and Growth.</p>

        <div className="improvement-category">
          <h3 className="section-label">Milestones</h3>
          <div className="tech-debt-grid">
            {ADMIN_MILESTONES.map((item) => (
              <div key={item.id} className="tech-debt-card">
                <div className="debt-header">
                  <span className="debt-id">{item.id}</span>
                  <span className="debt-effort">{item.effort}</span>
                </div>
                <h4 className="debt-title">{item.focus}</h4>
                <p className="debt-description">Owner: {item.owner}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="improvement-category">
          <h3 className="section-label">Navigation Map</h3>
          <div className="tech-debt-grid">
            <div className="tech-debt-card">
              <h4 className="debt-title">Primary Sections</h4>
              <ul className="improvement-deliverables">
                {ADMIN_NAV_ITEMS.map((item) => (
                  <li key={item}><IoEllipseOutline size={12} /> {item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="improvement-category">
          <h3 className="section-label">Page-Level Specs</h3>
          <div className="improvements-grid">
            {ADMIN_PAGE_SPECS.map((section) => (
              <div key={section.title} className="improvement-card">
                <h4 className="improvement-title">{section.title}</h4>
                <ul className="improvement-deliverables">
                  {section.items.map((item, i) => (
                    <li key={i}><IoEllipseOutline size={12} /> {item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Improvement Recommendations Section */}
      <div className="roadmap-section">
        <h2 className="section-title">Improvement Recommendations</h2>
        <p className="section-subtitle">Based on competitor analysis (OddAlerts, FotMob, modern betting apps) — January 2026</p>

        {/* High Priority */}
        <div className="improvement-category">
          <h3 className="category-title high-priority">High Priority</h3>
          <div className="improvements-grid">
            {HIGH_PRIORITY_IMPROVEMENTS.map((item) => (
              <div key={item.id} className="improvement-card">
                <div className="improvement-header">
                  <span className="improvement-id">{item.id}</span>
                  <span className="improvement-effort">{item.effort}</span>
                </div>
                <h4 className="improvement-title">{item.title}</h4>
                <p className="improvement-gap">{item.gap}</p>
                <ul className="improvement-deliverables">
                  {item.deliverables.map((d, i) => (
                    <li key={i}><IoEllipseOutline size={12} /> {d}</li>
                  ))}
                </ul>
                {item.references && item.references.length > 0 && (
                  <div className="improvement-refs">
                    {item.references.map((ref, i) => (
                      <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="ref-link">
                        {ref.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Medium Priority */}
        <div className="improvement-category">
          <h3 className="category-title medium-priority">Medium Priority</h3>
          <div className="improvements-grid">
            {MEDIUM_PRIORITY_IMPROVEMENTS.map((item) => (
              <div key={item.id} className="improvement-card">
                <div className="improvement-header">
                  <span className="improvement-id">{item.id}</span>
                  <span className="improvement-effort">{item.effort}</span>
                </div>
                <h4 className="improvement-title">{item.title}</h4>
                <p className="improvement-gap">{item.gap}</p>
                <ul className="improvement-deliverables">
                  {item.deliverables.map((d, i) => (
                    <li key={i}><IoEllipseOutline size={12} /> {d}</li>
                  ))}
                </ul>
                {item.references && item.references.length > 0 && (
                  <div className="improvement-refs">
                    {item.references.map((ref, i) => (
                      <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="ref-link">
                        {ref.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Lower Priority */}
        <div className="improvement-category">
          <h3 className="category-title lower-priority">Lower Priority / Nice-to-Have</h3>
          <div className="improvements-grid">
            {LOWER_PRIORITY_IMPROVEMENTS.map((item) => (
              <div key={item.id} className="improvement-card">
                <div className="improvement-header">
                  <span className="improvement-id">{item.id}</span>
                  <span className="improvement-effort">{item.effort}</span>
                </div>
                <h4 className="improvement-title">{item.title}</h4>
                <p className="improvement-gap">{item.gap}</p>
                <ul className="improvement-deliverables">
                  {item.deliverables.map((d, i) => (
                    <li key={i}><IoEllipseOutline size={12} /> {d}</li>
                  ))}
                </ul>
                {item.references && item.references.length > 0 && (
                  <div className="improvement-refs">
                    {item.references.map((ref, i) => (
                      <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" className="ref-link">
                        {ref.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* New Feature Ideas Section */}
      <div className="roadmap-section">
        <h2 className="section-title">New Feature Ideas (Backlog)</h2>
        <p className="section-subtitle">Based on competitor analysis and industry trends</p>
        <div className="feature-ideas-grid">
          {FEATURE_IDEAS.map((idea) => (
            <div key={idea.id} className="feature-idea-card">
              <div className="idea-header">
                <span className="idea-id">{idea.id}</span>
                <span className="idea-effort">{idea.effort}</span>
              </div>
              <h4 className="idea-title">{idea.title}</h4>
              <p className="idea-description">{idea.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Technical Debt Section */}
      <div className="roadmap-section">
        <h2 className="section-title">Technical Debt & Improvements</h2>
        <div className="tech-debt-grid">
          {TECH_DEBT_ITEMS.map((item) => (
            <div key={item.id} className="tech-debt-card">
              <div className="debt-header">
                <span className="debt-id">{item.id}</span>
                <span className="debt-effort">{item.effort}</span>
              </div>
              <h4 className="debt-title">{item.title}</h4>
              <p className="debt-description">{item.description}</p>
              {item.deliverables && (
                <ul className="debt-deliverables">
                  {item.deliverables.map((d, i) => (
                    <li key={i}><IoEllipseOutline size={12} /> {d}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Recommended Timeline Section */}
      <div className="roadmap-section">
        <h2 className="section-title">Recommended Timeline</h2>
        <div className="timeline-grid">
          {TIMELINE_SECTIONS.map((section, index) => (
            <div key={index} className={`timeline-card timeline-${index}`}>
              <h4 className="timeline-title">{section.title}</h4>
              <ol className="timeline-items">
                {section.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>

      {/* Sources Section */}
      <div className="roadmap-section">
        <h2 className="section-title">Competitor & Research Sources</h2>
        <div className="sources-grid">
          {SOURCES.map((source, index) => (
            <div key={index} className="source-card">
              <h4 className="source-category">{source.category}</h4>
              <ul className="source-links">
                {source.links.map((link, i) => (
                  <li key={i}>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      {link.name}
                    </a>
                    {link.note && <span className="source-note"> — {link.note}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
